import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { EmailEditorQuill } from 'modules/emailTemplates/emailEditors/EmailEditorQuill';

// Task Type IDs (from EnumerationV2.TaskType)
const TASK_TYPE_IDS = {
  VENDOR_EMAIL: 4,
  VENDOR_LETTER: 7,
  LETTER: 5,
};

const useStyles = makeStyles((theme) => ({
  taskInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  infoRow: {
    marginBottom: theme.spacing(0.5),
  },
  templateInfo: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.info.light + '20',
    borderRadius: theme.shape.borderRadius,
  },
  editorWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

// Simple email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmail = (email) => {
  return EMAIL_REGEX.test(email);
};

const validateEmailList = (emailList) => {
  if (!emailList || !emailList.trim()) return true; // Empty is valid for CC
  const emails = emailList.split(',').map(e => e.trim());
  return emails.every(validateEmail);
};

const TaskEmailModal = ({ open, task, letterTemplate, onClose, onSubmit, isLoading }) => {
  const classes = useStyles();
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [includeCc, setIncludeCc] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [ccEmailError, setCcEmailError] = useState('');

  // Initialize form when modal opens or template loads
  useEffect(() => {
    if (open && task) {
      // Set recipient email (employee or vendor)
      const isVendorTask = task.taskTypeId === TASK_TYPE_IDS.VENDOR_EMAIL ||
                           task.taskTypeId === TASK_TYPE_IDS.VENDOR_LETTER;
      if (isVendorTask) {
        setToEmail(task.vendorEmail || '');
      } else {
        setToEmail(task.eeEmail || '');
      }

      // Set subject and body from template or defaults
      if (letterTemplate) {
        setSubject(letterTemplate.subject || `Letter for ${task.eeFullName || 'Employee'}`);
        setBodyHtml(letterTemplate.content || '');
      } else {
        setSubject(task.title || 'Email regarding your relocation');
        setBodyHtml('');
      }

      setCcEmail('');
      setIncludeCc(false);
      setEmailError('');
      setCcEmailError('');
    }
  }, [open, task, letterTemplate]);

  const handleToEmailChange = (e) => {
    const value = e.target.value;
    setToEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleCcEmailChange = (e) => {
    const value = e.target.value;
    setCcEmail(value);
    if (value && !validateEmailList(value)) {
      setCcEmailError('Please enter valid email addresses (comma-separated)');
    } else {
      setCcEmailError('');
    }
  };

  const handleSend = () => {
    // Final validation before submit
    if (!validateEmail(toEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (includeCc && ccEmail && !validateEmailList(ccEmail)) {
      setCcEmailError('Please enter valid email addresses (comma-separated)');
      return;
    }

    const emailData = {
      toEmail,
      ccEmail: includeCc ? ccEmail : '',
      subject,
      bodyHtml,
    };
    onSubmit(emailData);
  };

  const handleBodyChange = (html) => {
    setBodyHtml(html);
  };

  if (!task) {
    return null;
  }

  const isLetter = task.taskTypeId === TASK_TYPE_IDS.LETTER ||
                   task.taskTypeId === TASK_TYPE_IDS.VENDOR_LETTER;
  const isValid = toEmail.trim() &&
                  subject.trim() &&
                  bodyHtml.trim() &&
                  !emailError &&
                  !ccEmailError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isLetter ? 'Send Letter' : 'Send Email'}: {task.title || 'Untitled Task'}
      </DialogTitle>
      <DialogContent>
        <Box className={classes.taskInfo}>
          <Typography variant="body2" className={classes.infoRow}>
            <strong>File:</strong> {task.fileNumber || 'N/A'} - {task.eeFullName || 'N/A'}
          </Typography>
          <Typography variant="body2" className={classes.infoRow}>
            <strong>Client:</strong> {task.clientName || 'N/A'}
          </Typography>
          {task.vendorName && (
            <Typography variant="body2" className={classes.infoRow}>
              <strong>Vendor:</strong> {task.vendorName}
            </Typography>
          )}
        </Box>

        {isLetter && letterTemplate && (
          <Box className={classes.templateInfo}>
            <Typography variant="caption" color="textSecondary">
              Letter template loaded: {letterTemplate.name || 'Unnamed template'}
            </Typography>
          </Box>
        )}

        <TextField
          label="To"
          fullWidth
          variant="outlined"
          value={toEmail}
          onChange={handleToEmailChange}
          margin="normal"
          disabled={isLoading}
          error={!!emailError}
          helperText={emailError || (isLetter ? 'Recipient for letter' : 'Recipient email address')}
          size="small"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={includeCc}
              onChange={(e) => setIncludeCc(e.target.checked)}
              disabled={isLoading}
            />
          }
          label="Include CC"
        />

        {includeCc && (
          <TextField
            label="CC"
            fullWidth
            variant="outlined"
            value={ccEmail}
            onChange={handleCcEmailChange}
            margin="normal"
            disabled={isLoading}
            error={!!ccEmailError}
            helperText={ccEmailError || "Optional CC recipients (comma-separated)"}
            size="small"
          />
        )}

        <TextField
          label="Subject"
          fullWidth
          variant="outlined"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="normal"
          disabled={isLoading}
          size="small"
        />

        <Box className={classes.editorWrapper}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            {isLetter ? 'Letter Content' : 'Email Message'}
          </Typography>
          <EmailEditorQuill
            initialHtml={bodyHtml}
            onHtmlChange={handleBodyChange}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={isLoading || !isValid}
        >
          {isLetter ? 'Send Letter' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TaskEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  task: PropTypes.shape({
    taskId: PropTypes.number,
    title: PropTypes.string,
    fileNumber: PropTypes.string,
    eeFullName: PropTypes.string,
    eeEmail: PropTypes.string,
    clientName: PropTypes.string,
    vendorName: PropTypes.string,
    vendorEmail: PropTypes.string,
    taskTypeId: PropTypes.number,
  }),
  letterTemplate: PropTypes.shape({
    name: PropTypes.string,
    subject: PropTypes.string,
    content: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

TaskEmailModal.defaultProps = {
  task: null,
  letterTemplate: null,
  isLoading: false,
};

export default TaskEmailModal;
